import * as React from 'react';
import { DimensionValue, Platform, StyleSheet } from 'react-native';
import { translate } from 'secullum-i18n';
import {
  Card,
  ErrorMessage,
  Message,
  Text,
  TextBox,
  isTablet,
  DropDown
} from 'secullum-react-native-ui';
import BotaoDestaque from '../../components/BotaoDestaque';
import MapaBatida from '../../components/MapaBatida';
import {
  MessageContextType,
  withMessageContext
} from '../../components/MessageProvider';
import ModalAvisoInclusaoPontoSuspeita from '../../components/ModalAvisoInclusaoPontoSuspeita';
import Api from '../../modules/api';
import { validarBatidaDentroPerimetro } from '../../modules/geolocation';
import { getTheme } from '../../modules/layout';
import { getMessage, Keys } from '../../modules/messages';
import { verificarEAtivarDivergenciaHorarioAsync } from '../../modules/modificacaoDataHora';
import { verificarEAtivarDivergenciaFusoFuncionarioAsync } from '../../modules/modificacaoFusoHorario';
import {
  Atividade,
  PerimetroAutorizado,
  QualidadeVidaTrabalho,
  RegistroPontoPendencia,
  RegistroPontoPendenciaStatus,
  TipoCameraFotoPonto,
  TipoInclusao,
  TipoPlano,
  TipoRespostaQualidadeVidaTrabalho,
  TipoSolicitacao
} from '../../modules/types';
import ListaUltimasPendencias from './ListaUltimasPendencias';
import ModalConfirmarNovaInclusao from './ModalConfirmarNovaInclusao';
import ModalQualidadeVidaTrabalho from './ModalQualidadeVidaTrabalho';

interface Props {
  coordenadas: {
    latitude: number;
    longitude: number;
    accuracy: number | null;
  } | null;
  endereco: string | null;
  localizacaoStatus: string | null;
  dispositivoAutorizadoInclusaoPonto: boolean;
  identificacaoDispositivo: string | null;
  centralWeb: boolean;
  exibirUltimosRegistros: boolean;
  permiteInclusaoPontoSemLocalizacao: boolean;
  tipoInclusao: TipoInclusao;
  checkLocalizacaoFicticiaAsync?: () => Promise<boolean>;
  onExigirCapturaFoto(tipoCamera: TipoCameraFotoPonto, mensagem: string): void;
  onExibirMapaNovaInclusaoPonto: () => void;
  onErroConexao?: (mensagem: string, onTentarNovamente: () => void) => void;
  messageContext: MessageContextType;
  carregandoMapa: boolean;
  onCarregandoMapaChange: (carregandoMapa: boolean) => void;
}

interface State {
  horaServidor: Date;
  justificativaAutomatica: boolean;
  justificativa: string | null;
  exigirCapturaFotoPonto: boolean;
  tipoCameraCapturaFotoPonto: TipoCameraFotoPonto;
  reconhecerFace: boolean;
  fotoTemporaria: string | null;
  exibirControlesInclusaoPonto: boolean;
  funcionarioAfastado: boolean;
  listaUltimasPendencias: Array<RegistroPontoPendencia>;
  exibirModalConfirmacaoNovaInclusao: boolean;
  exibirModalQualidadeVidaTrabalho: boolean;
  erros: { [keyof: string]: string };
  erroGeral: string | null;
  resultado: 'sucesso' | 'erro' | null;
  perimetrosAutorizados: PerimetroAutorizado[];
  foraDoPerimetro: boolean;
  abrirModalAvisoInclusaoPontoSuspeita: boolean;
  somentePerimetrosAutorizados: boolean;
  qualidadeVidaTrabalho: QualidadeVidaTrabalho;
  atividades: Atividade[];
  atividadeId: number;
  localizacaoFicticiaDetectada: boolean;
  informacoesNovaInclusaoManualCarregadas: boolean;
}

class CardInclusaoPontoAssincrona extends React.Component<Props, State> {
  timeoutAtualizacaoAutomatica: NodeJS.Timeout | null = null;

  state: State = {
    horaServidor: new Date(),
    justificativaAutomatica: true,
    justificativa: null,
    exigirCapturaFotoPonto: false,
    tipoCameraCapturaFotoPonto: TipoCameraFotoPonto.LivreEscolhaFuncionario,
    reconhecerFace: false,
    fotoTemporaria: null,
    exibirControlesInclusaoPonto: true,
    funcionarioAfastado: false,
    listaUltimasPendencias: [],
    exibirModalConfirmacaoNovaInclusao: false,
    exibirModalQualidadeVidaTrabalho: false,
    erros: { justificativa: '' },
    erroGeral: null,
    resultado: null,
    perimetrosAutorizados: [],
    foraDoPerimetro: false,
    abrirModalAvisoInclusaoPontoSuspeita: false,
    somentePerimetrosAutorizados: false,
    qualidadeVidaTrabalho: {
      habilitado: false,
      pergunta: '',
      respostaBem: '',
      respostaBemDescricao: '',
      respostaExcelente: '',
      respostaExcelenteDescricao: '',
      respostaMal: '',
      respostaMalDescricao: '',
      respostaMuitoBem: '',
      respostaMuitoBemDescricao: '',
      respostaNaoEstouBem: '',
      respostaNaoEstouBemDescricao: '',
      exibirPesquisa: 0
    },
    atividades: [],
    atividadeId: 0,
    localizacaoFicticiaDetectada: false,
    informacoesNovaInclusaoManualCarregadas: false
  };

  async componentDidMount() {
    const { messageContext, exibirUltimosRegistros } = this.props;

    if (exibirUltimosRegistros) {
      this.setState({ exibirControlesInclusaoPonto: false });
    }

    await messageContext.withMessage(Keys.NOVA_INCLUSAO, () =>
      this.carregarInformacoesNovaInclusaoManual({
        exibirControlesInclusaoPonto: false
      })
    );

    await messageContext.withMessage(Keys.ULTIMAS_BATIDAS, () =>
      this.carregarUltimasBatidasAsync({
        exibirControlesInclusaoPonto: false
      })
    );

    await messageContext.withMessage(Keys.LOCALIZACAO_FICTICIA, () =>
      this.utilizaLocalizacaoFicticiaAsync()
    );
  }

  componentWillUnmount() {
    if (this.timeoutAtualizacaoAutomatica) {
      clearTimeout(this.timeoutAtualizacaoAutomatica);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { coordenadas } = this.props;
    const { perimetrosAutorizados, somentePerimetrosAutorizados } = this.state;

    // Não valida perímetro quando é uma atividade.
    if (this.tipoInclusaoAtividade) {
      return;
    }

    if (
      coordenadas &&
      prevProps.coordenadas !== coordenadas &&
      somentePerimetrosAutorizados
    ) {
      const foraDoPeriometro = !validarBatidaDentroPerimetro(
        coordenadas.latitude,
        coordenadas.longitude,
        perimetrosAutorizados
      );

      if (foraDoPeriometro !== this.state.foraDoPerimetro) {
        this.setState({
          foraDoPerimetro: foraDoPeriometro
        });
      }
    }
  }

  handleChange = (name: string, value: any) => {
    this.setState(state => ({
      ...state,
      [name]: value,
      erros: {
        ...state.erros,
        [name]: ''
      }
    }));
  };

  handleNovaInclusao = async () => {
    const existeRegistroPendente = this.verificarExistenciaRegistroPendente(
      this.state.listaUltimasPendencias
    );

    if (existeRegistroPendente) {
      this.setState({ exibirModalConfirmacaoNovaInclusao: true });
    } else {
      await this.carregarInformacoesNovaInclusaoManual({
        exibirControlesInclusaoPonto: true
      });
    }
  };

  handleModalConfirmarNovaInclusaoCancelar = () => {
    this.setState({ exibirModalConfirmacaoNovaInclusao: false });
  };

  handleModalConfirmarNovaInclusaoContinuar = async () => {
    this.setState({ exibirModalConfirmacaoNovaInclusao: false });

    await this.carregarInformacoesNovaInclusaoManual({
      exibirControlesInclusaoPonto: true
    });
  };

  handleIncluirPontoSucesso = () => {
    this.setState(
      {
        resultado: 'sucesso',
        fotoTemporaria: null,
        exibirControlesInclusaoPonto: false,
        exibirModalQualidadeVidaTrabalho: false
      },
      async () => {
        await this.carregarUltimasBatidasAsync({
          exibirControlesInclusaoPonto: false
        });
      }
    );
  };

  handleModalQualidadeVidaTrabalhoConcluir = async (
    respostaPesquisa: TipoRespostaQualidadeVidaTrabalho
  ) => {
    const api = new Api();

    api.postIncluirRespostaQualidadeVidaTrabalho(
      {
        respostaPesquisa: respostaPesquisa
      },
      {
        onSuccess: () => this.handleIncluirPontoSucesso(),
        onError: err => {
          if (err.name === 'TimeoutError' && this.props.onErroConexao) {
            this.props.onErroConexao(
              err.message,
              async () =>
                await this.handleModalQualidadeVidaTrabalhoConcluir(
                  respostaPesquisa
                )
            );
          } else {
            this.setState({ resultado: 'erro', erroGeral: err.message });
          }
        },
        onValidationError: (errorList, errorObj) =>
          this.setState({ erros: errorObj })
      }
    );
  };

  handleAtualizacaoAutomaticaUltimosRegistros = async () => {
    const listaUltimasPendencias = await this.carregarListaUltimasPendencias();

    if (listaUltimasPendencias === undefined) {
      return;
    }

    const existeRegistroPendente = this.verificarExistenciaRegistroPendente(
      listaUltimasPendencias
    );

    if (existeRegistroPendente) {
      this.timeoutAtualizacaoAutomatica = setTimeout(
        this.handleAtualizacaoAutomaticaUltimosRegistros,
        10 * 1000 // Depois da primeira tentativa, fica tentando a cada 10s
      );
    }
  };

  handleIncluirPonto = async () => {
    await this.utilizaLocalizacaoFicticiaAsync();

    const {
      justificativaAutomatica,
      justificativa,
      exigirCapturaFotoPonto,
      tipoCameraCapturaFotoPonto
    } = this.state;

    if (!justificativaAutomatica && !justificativa) {
      this.setState({
        erros: {
          justificativa: translate('Preencha o campo corretamente') + '.'
        }
      });

      return;
    }

    if (exigirCapturaFotoPonto) {
      const { tipoInclusao, onExigirCapturaFoto } = this.props;
      const tipoRegistro =
        tipoInclusao === TipoInclusao.IncluirPonto ? 'ponto' : 'atividade';
      const mensagemCapturarFoto = translate(
        `A empresa onde você trabalha solicita uma foto sua a cada registro de ${tipoRegistro}.`
      );

      onExigirCapturaFoto(tipoCameraCapturaFotoPonto, mensagemCapturarFoto);
    } else {
      this.incluirPonto();
    }
  };

  utilizaLocalizacaoFicticiaAsync = async () => {
    const { checkLocalizacaoFicticiaAsync } = this.props;

    if (
      checkLocalizacaoFicticiaAsync &&
      (await checkLocalizacaoFicticiaAsync())
    ) {
      this.setState({ localizacaoFicticiaDetectada: true });
      return true;
    }

    return false;
  };

  verificarDivergenciasAsync = async (horaServidor: Date) => {
    const fusoDivergente =
      await verificarEAtivarDivergenciaFusoFuncionarioAsync(
        'online',
        new Date(horaServidor)
      );

    if (fusoDivergente) {
      return;
    }

    await verificarEAtivarDivergenciaHorarioAsync();
  };

  incluirPonto = async (fotoCapturada: string | null = null) => {
    const {
      coordenadas,
      endereco,
      centralWeb,
      identificacaoDispositivo,
      permiteInclusaoPontoSemLocalizacao
    } = this.props;
    const { justificativa, fotoTemporaria, foraDoPerimetro, atividadeId } =
      this.state;

    const foto = fotoCapturada || fotoTemporaria;

    const localizacaoFicticiaDetectada =
      await this.utilizaLocalizacaoFicticiaAsync();

    if (!coordenadas && !permiteInclusaoPontoSemLocalizacao) {
      return;
    }

    await new Api().postIncluirPonto(
      {
        justificativa,
        latitude: coordenadas ? coordenadas.latitude : null,
        longitude: coordenadas ? coordenadas.longitude : null,
        precisao: coordenadas ? coordenadas.accuracy || 0 : null,
        endereco,
        foto,
        marcacaoOffline: false,
        viaCentralWeb: centralWeb,
        identificacaoDispositivo: identificacaoDispositivo,
        foraDoPerimetro: foraDoPerimetro,
        utilizaLocalizacaoFicticia: localizacaoFicticiaDetectada,
        horaFoiModificada: false,
        fusoFoiModificado: false,
        atividadeId: this.tipoInclusaoAtividade ? atividadeId : null
      },
      {
        esconderSpinner: false
      },
      {
        onSuccess: resp => {
          if (resp?.exibirQvt) {
            this.setState({ exibirModalQualidadeVidaTrabalho: true });
          } else {
            this.handleIncluirPontoSucesso();
          }
        },
        onError: err => {
          if (err.name === 'TimeoutError' && this.props.onErroConexao) {
            this.props.onErroConexao(err.message, () =>
              this.handleIncluirPonto()
            );
          } else {
            this.setState({
              resultado: 'erro',
              erroGeral: err.message,
              fotoTemporaria: foto,
              exigirCapturaFotoPonto:
                this.verificarExigenciaCapturaFotoPonto(foto)
            });
          }
        },
        onValidationError: (errorList, errorObj) => {
          const erroGeral = errorList.filter(erro => erro.property === null);
          const temErroGeral = erroGeral.length > 0;

          this.setState({
            resultado: temErroGeral ? 'erro' : null,
            erroGeral: temErroGeral ? erroGeral[0].message : null,
            erros: errorObj,
            fotoTemporaria: foto,
            exigirCapturaFotoPonto:
              this.verificarExigenciaCapturaFotoPonto(foto)
          });
        }
      }
    );
  };

  carregarUltimasBatidasAsync = async (options: {
    exibirControlesInclusaoPonto: boolean;
  }) => {
    const listaUltimasPendencias = await this.carregarListaUltimasPendencias(
      () => this.carregarInformacoesNovaInclusaoManual(options)
    );

    // Erro ao carregar lista de pendências
    if (listaUltimasPendencias === undefined) {
      return;
    }

    const existeRegistroPendente = this.verificarExistenciaRegistroPendente(
      listaUltimasPendencias
    );

    if (existeRegistroPendente) {
      this.timeoutAtualizacaoAutomatica = setTimeout(
        this.handleAtualizacaoAutomaticaUltimosRegistros,
        70 * 1000 // 1m10s para dar tempo do servidor processar inicialmente
      );
    }
  };

  carregarInformacoesNovaInclusaoManual = async (options: {
    exibirControlesInclusaoPonto: boolean;
  }) => {
    this.setState({ informacoesNovaInclusaoManualCarregadas: false });

    const api = new Api();

    await api.getInformacoesNovaInclusaoManual({
      onSuccess: async resp => {
        this.setState({
          horaServidor: resp.horaServidor,
          justificativaAutomatica: resp.justificativaAutomatica,
          justificativa: null,
          exigirCapturaFotoPonto:
            api.funcionario.state.dados.plano >= TipoPlano.Pro &&
            resp.exigirCapturaFotoPonto,
          tipoCameraCapturaFotoPonto: resp.tipoCameraCapturaFotoPonto,
          reconhecerFace: resp.reconhecerFace,
          funcionarioAfastado: resp.funcionarioAfastado,
          somentePerimetrosAutorizados: resp.somentePerimetrosAutorizados,
          perimetrosAutorizados: resp.perimetrosAutorizados,
          qualidadeVidaTrabalho: resp.qualidadeVidaTrabalho,
          atividades: resp.atividades,
          atividadeId: resp.atividades.length > 0 ? resp.atividades[0].id : 0,
          informacoesNovaInclusaoManualCarregadas: true
        });

        // Sec-issues#12356: Para inclusão de atividades não realizamos a validação de perimetros autorizados
        if (
          resp.somentePerimetrosAutorizados &&
          this.props.tipoInclusao === TipoInclusao.IncluirPonto &&
          this.props.coordenadas
        ) {
          this.setState({
            foraDoPerimetro: !validarBatidaDentroPerimetro(
              this.props.coordenadas.latitude,
              this.props.coordenadas.longitude,
              resp.perimetrosAutorizados
            )
          });
        }

        if (options.exibirControlesInclusaoPonto) {
          this.setState({ exibirControlesInclusaoPonto: true }, () => {
            this.props.onExibirMapaNovaInclusaoPonto();
          });
        }

        if (
          options.exibirControlesInclusaoPonto ||
          this.state.exibirControlesInclusaoPonto
        ) {
          await this.verificarDivergenciasAsync(resp.horaServidor);
        }

        if (this.tipoInclusaoAtividade && resp.atividades.length === 0) {
          this.setState({
            resultado: 'erro',
            erroGeral: translate(
              'Você não possui permissão para inclusão de atividade'
            )
          });
        }
      },
      onError: (err: Error) => {
        if (err.name === 'TimeoutError' && this.props.onErroConexao) {
          this.props.onErroConexao(err.message, () =>
            this.carregarInformacoesNovaInclusaoManual({
              exibirControlesInclusaoPonto: options.exibirControlesInclusaoPonto
            })
          );
        } else {
          this.setState({
            resultado: 'erro',
            erroGeral: err.message
          });
        }
      }
    });
  };

  carregarListaUltimasPendencias = async (
    funcaoTentarNovamente?: () => void
  ) => {
    const { tipoInclusao } = this.props;
    let retorno: Array<RegistroPontoPendencia> | undefined;

    const api = new Api();

    await api.getListaUltimasPendencias(tipoInclusao, {
      onSuccess: (lista: Array<RegistroPontoPendencia>) => {
        this.setState({
          listaUltimasPendencias: lista
        });

        retorno = lista;
      },
      onError: (err: Error) => {
        if (err.name === 'TimeoutError' && this.props.onErroConexao) {
          this.props.onErroConexao(
            err.message,
            funcaoTentarNovamente
              ? funcaoTentarNovamente
              : () => this.carregarListaUltimasPendencias()
          );
        } else {
          this.setState({
            resultado: 'erro',
            erroGeral: err.message
          });
        }
      }
    });

    return retorno;
  };

  verificarExigenciaCapturaFotoPonto = (fotoTemporaria: string | null) => {
    return (
      (this.state.exigirCapturaFotoPonto && this.state.reconhecerFace) ||
      (this.state.exigirCapturaFotoPonto && !fotoTemporaria)
    );
  };

  verificarExistenciaRegistroPendente = (
    lista: Array<RegistroPontoPendencia>
  ) => {
    const registroPendente = lista.find(
      x => x.status === RegistroPontoPendenciaStatus.Pendente
    );

    return registroPendente !== undefined;
  };

  alturaMapa = () => {
    const { justificativaAutomatica, foraDoPerimetro } = this.state;
    const { permiteInclusaoPontoSemLocalizacao } = this.props;

    if (permiteInclusaoPontoSemLocalizacao) {
      return undefined;
    }

    // A tipagem do StyleSheet mudou na versão do ReactNative 0.73.4 de "string | number | null" para o tipo DimensionValue
    // que foi pensado para ser usado no ReactNative mobile, onde não aceita string literais como "10px" por exemplo.
    // Porém no web, esses valores são aceitos pelo navegador e funcionam normalmente.
    // Sendo assim, daremos um cast da string para DimensionValue para que o typescript reconheça como um valor válido.
    if (Platform.OS === 'web') {
      return `calc(100vh - ${
        justificativaAutomatica
          ? foraDoPerimetro
            ? 294
            : 262
          : foraDoPerimetro
          ? 372
          : 323
      }px)` as DimensionValue;
    }

    if (isTablet()) {
      return justificativaAutomatica
        ? foraDoPerimetro
          ? 382
          : 414
        : foraDoPerimetro
        ? 307
        : 339;
    } else {
      return justificativaAutomatica
        ? foraDoPerimetro
          ? 294
          : 314
        : foraDoPerimetro
        ? 209
        : 239;
    }
  };

  get tipoInclusaoAtividade() {
    return this.props.tipoInclusao === TipoInclusao.IncluirAtividade;
  }

  obterUltimasPendencias = () => {
    const { listaUltimasPendencias, atividades } = this.state;

    if (!this.tipoInclusaoAtividade || !atividades.length) {
      return listaUltimasPendencias;
    }

    return listaUltimasPendencias.map(pendencia => {
      if (!pendencia.atividadeId) {
        return pendencia;
      }

      const atividade = atividades.find(y => y.id === pendencia.atividadeId);

      return {
        ...pendencia,
        atividadeDescricaoAbreviada: atividade?.descricaoAbreviada || null
      };
    });
  };

  obterListaAtividades = () => {
    const { atividades } = this.state;

    if (!this.tipoInclusaoAtividade) {
      return [];
    }

    return atividades.map((atividade, index) => ({
      nativeID: 'atividade-' + index,
      label: atividade.descricao,
      value: atividade.id
    }));
  };

  obterTipoAvisoInclusaoPonto = () => {
    const { funcionarioAfastado, localizacaoFicticiaDetectada } = this.state;

    if (funcionarioAfastado) {
      return TipoSolicitacao.Afastamento;
    }

    if (localizacaoFicticiaDetectada) {
      return TipoSolicitacao.UtilizaLocalizacaoFicticia;
    }

    return TipoSolicitacao.ForaDoPerimetro;
  };

  obterMensagemSucesso = () => {
    if (this.tipoInclusaoAtividade) {
      return translate('Inclusão de atividade efetuada com êxito.');
    }

    return `${translate(
      'Inclusão de ponto efetuada com êxito.'
    )}\n\n${translate(
      'Dentro de alguns instantes estará disponível em seu Cartão Ponto.'
    )}`;
  };

  obterMensagensNovaInclusao = () => {
    if (this.tipoInclusaoAtividade) {
      return {
        aviso: translate(
          'Já existe uma atividade realizada que está aguardando processamento.'
        ),
        confirmacao: translate('Deseja ainda assim incluir um novo registro?')
      };
    }

    return {
      aviso: translate(
        'Já existe uma inclusão de ponto realizada que está aguardando processamento.'
      ),
      confirmacao: translate(
        'Caso você registre uma nova inclusão, esta poderá ser considerada como batida duplicada.'
      )
    };
  };

  deveExibirModalAviso = () => {
    const {
      foraDoPerimetro,
      localizacaoFicticiaDetectada,
      funcionarioAfastado
    } = this.state;

    return (
      foraDoPerimetro || localizacaoFicticiaDetectada || funcionarioAfastado
    );
  };

  obterInformacoesBotaoIncluir = () => {
    const { atividades } = this.state;

    if (this.tipoInclusaoAtividade) {
      return {
        nativeID: 'localizacao-incluir-atividade',
        text: translate('Incluir Atividade'),
        disabled: !atividades.length,
        loading: false
      };
    }

    const {
      permiteInclusaoPontoSemLocalizacao,
      carregandoMapa,
      messageContext
    } = this.props;

    const nativeID = 'localizacao-incluir-ponto';
    const textoPadrao = 'Incluir Ponto';

    // Case 143192: bloqueia até o GET de configuração retornar para garantir que
    // `exigirCapturaFotoPonto` esteja em sincronia com o servidor antes do POST.
    if (!this.state.informacoesNovaInclusaoManualCarregadas) {
      return {
        nativeID,
        text: `${getMessage(Keys.NOVA_INCLUSAO)}...`,
        disabled: true,
        loading: true
      };
    }

    if (permiteInclusaoPontoSemLocalizacao) {
      const mensagem = messageContext.lastMessageKeyAdded
        ? `${getMessage(messageContext.lastMessageKeyAdded)}...`
        : translate(textoPadrao);

      return {
        nativeID,
        text: mensagem,
        disabled: !!messageContext.lastMessageKeyAdded,
        loading: !!messageContext.lastMessageKeyAdded
      };
    }

    if (carregandoMapa) {
      const mensagem = messageContext.lastMessageKeyAdded
        ? getMessage(messageContext.lastMessageKeyAdded)
        : translate('Carregando mapa');

      return {
        nativeID,
        text: `${mensagem}...`,
        disabled: true,
        loading: true
      };
    }

    return {
      nativeID,
      text: translate(textoPadrao),
      disabled: false,
      loading: false
    };
  };

  render() {
    const {
      localizacaoStatus,
      dispositivoAutorizadoInclusaoPonto,
      tipoInclusao,
      coordenadas,
      endereco,
      onCarregandoMapaChange
    } = this.props;

    const {
      horaServidor,
      localizacaoFicticiaDetectada,
      foraDoPerimetro,
      erroGeral,
      resultado,
      justificativaAutomatica,
      justificativa,
      erros,
      atividadeId,
      exibirControlesInclusaoPonto,
      exibirModalQualidadeVidaTrabalho,
      exibirModalConfirmacaoNovaInclusao,
      abrirModalAvisoInclusaoPontoSuspeita
    } = this.state;

    if (!dispositivoAutorizadoInclusaoPonto) {
      const tipoTexto =
        tipoInclusao === TipoInclusao.IncluirAtividade ? 'atividade' : 'ponto';

      return (
        <Card>
          <Card.Section>
            <Text
              color={theme.textColor1}
              nativeID="localizacao-mensagem-dispositivo-restrito"
            >
              {translate(
                `A empresa onde você trabalha não autoriza a inclusão de ${tipoTexto} a partir deste dispositivo.`
              )}
              {'\n\n'}
              {translate('Por favor, entre em contato com sua empresa.')}
            </Text>
          </Card.Section>
        </Card>
      );
    }

    if (localizacaoStatus) {
      return (
        <Card>
          <Card.Section style={styles.sectionSemAcessoLocalizacao}>
            <Text
              nativeID="localizacao-mensagem-erro"
              bold
              color={theme.errorColor}
              size={isTablet() ? 30 : 26}
            >
              {localizacaoStatus}
            </Text>
          </Card.Section>
        </Card>
      );
    }

    const { aviso, confirmacao } = this.obterMensagensNovaInclusao();
    const { nativeID, text, disabled, loading } =
      this.obterInformacoesBotaoIncluir();

    return (
      <>
        {exibirControlesInclusaoPonto && (
          <Card>
            <Card.Section style={styles.sectionMapa}>
              <MapaBatida
                coordenadas={coordenadas}
                endereco={endereco}
                mostrarRelogio
                data={horaServidor}
                alturaMapa={this.alturaMapa()}
                utilizaLocalizacaoFicticia={localizacaoFicticiaDetectada}
                foraDoPerimetro={foraDoPerimetro}
                onCarregandoMapaChange={onCarregandoMapaChange}
              />
            </Card.Section>

            {!justificativaAutomatica && (
              <Card.Section style={styles.sectionJustificativa}>
                <TextBox
                  nativeID="localizacao-justificativa"
                  label={translate('Justificativa')}
                  value={justificativa || ''}
                  onChange={value => this.handleChange('justificativa', value)}
                  returnKeyType="done"
                  maxLength={100}
                />
                <ErrorMessage
                  nativeID="localizacao-erro-justificativa"
                  message={erros.justificativa}
                />
              </Card.Section>
            )}

            {this.tipoInclusaoAtividade && (
              <Card.Section style={styles.sectionJustificativa}>
                <DropDown
                  nativeID="localizacao-tipo-atividade"
                  label={translate('Tipo de Atividade')}
                  items={this.obterListaAtividades()}
                  value={atividadeId}
                  onChange={id => this.handleChange('atividadeId', id)}
                  emptyMessage={translate('Sua busca não retornou resultados')}
                />
              </Card.Section>
            )}

            <Card.Section style={Platform.OS === 'web' && styles.sectionButton}>
              <BotaoDestaque
                nativeID={nativeID}
                text={text}
                loading={loading}
                style={Platform.OS === 'web' && styles.button}
                disabled={disabled}
                onPress={() => {
                  if (this.deveExibirModalAviso()) {
                    this.setState({
                      abrirModalAvisoInclusaoPontoSuspeita: true
                    });
                  } else {
                    this.handleIncluirPonto();
                  }
                }}
                disabledLabelColor={theme.textColor3}
              />
            </Card.Section>
          </Card>
        )}

        <ListaUltimasPendencias
          onNovaInclusao={this.handleNovaInclusao}
          onAtualizar={this.carregarListaUltimasPendencias}
          listaUltimasPendencias={this.obterUltimasPendencias()}
          exbirBotaoNovaInclusao={!exibirControlesInclusaoPonto}
        />

        <ModalAvisoInclusaoPontoSuspeita
          visible={abrirModalAvisoInclusaoPontoSuspeita}
          onCancelar={() => {
            this.setState({ abrirModalAvisoInclusaoPontoSuspeita: false });
          }}
          onConcluir={() => {
            this.setState({ abrirModalAvisoInclusaoPontoSuspeita: false });
            this.handleIncluirPonto();
          }}
          tipoAviso={this.obterTipoAvisoInclusaoPonto()}
        />

        <Message
          nativeID="localizacao-sucesso"
          message={this.obterMensagemSucesso()}
          textStyle={styles.textoSucesso}
          visible={resultado === 'sucesso'}
          onRequestClose={() => {
            this.setState({ resultado: null });
          }}
        />

        <Message
          nativeID="localizacao-erro"
          type="warning"
          message={erroGeral ? erroGeral : ''}
          visible={resultado === 'erro'}
          onRequestClose={() => {
            this.setState({ resultado: null, erroGeral: '' });
          }}
        />

        <ModalConfirmarNovaInclusao
          mensagem={aviso}
          mensagemConfirmacao={confirmacao}
          visivel={exibirModalConfirmacaoNovaInclusao}
          onCancelar={this.handleModalConfirmarNovaInclusaoCancelar}
          onContinuar={this.handleModalConfirmarNovaInclusaoContinuar}
        />

        {this.state.qualidadeVidaTrabalho && (
          <ModalQualidadeVidaTrabalho
            dados={this.state.qualidadeVidaTrabalho}
            visivel={exibirModalQualidadeVidaTrabalho}
            onConcluir={this.handleModalQualidadeVidaTrabalhoConcluir}
          />
        )}
      </>
    );
  }
}

const theme = getTheme();

const styles = StyleSheet.create({
  sectionSemAcessoLocalizacao: {
    paddingVertical: 100,
    alignItems: 'center',
    justifyContent: 'center'
  },
  sectionMapa: {
    padding: 0
  },
  sectionJustificativa: {
    paddingVertical: 10
  },
  sectionButton: {
    alignItems: 'center'
  },
  textoSucesso: {
    textAlign: 'center',
    fontSize: isTablet() ? 25 : 16
  },
  button: {
    width: '100%',
    maxWidth: 350
  },
  text: {
    fontSize: isTablet() ? 18 : 14,
    color: theme.textColor3
  }
});

export type CardInclusaoPontoAssincronaRef = CardInclusaoPontoAssincrona;
export default withMessageContext({ forwardRef: true })(
  CardInclusaoPontoAssincrona
);
